'use client'

// imports
import { useState } from 'react';
import Link from 'next/link';

// lib files
import { VideoTypeEnum } from '@/lib/types/api/video';
import { FeaturedPreview } from '@/lib/types/api/show-data';
import { ContentServiceHeroSpotlightShowContent } from '@/lib/types/api/content-service';

// components
import ITSImage from '@/components/ITSImage/ITSImage';
import LinkButton from '@/components/Button/LinkButton';
import KabobMenu from '@/components/KabobMenu/KabobMenu';
import MezzanineVideoInitializer from '@/components/MezzanineVideoInitializer/MezzanineVideoInitializer';
import MyListButton from '@/components/Button/MyList/MyListButton';
import { StationData } from '@/lib/types/api/stations-data';
import VideoPlayer from  '@/components/VideoPlayer/VideoPlayer';

// svgs
import PlayIcon from "@/public/svg/play.svg";
import ArrowRightIcon from '@/public/svg/arrow-right.svg';

// styles
import styles from './ShowHero.module.scss';

interface ShowHeroProps {
  isSVP: boolean;
  stationData?: StationData;
  featuredPreview?: FeaturedPreview;
  showHeroSpotlightContent: ContentServiceHeroSpotlightShowContent;
}

const ShowHero: React.FC<ShowHeroProps> = (props) => {
  const {
    isSVP,
    stationData,
    showHeroSpotlightContent,
    featuredPreview,
  } = props;
  const [showVideo, setShowVideo] = useState(false);
  const { show, links, background_image } = showHeroSpotlightContent;
  const { slug, title, images, description_short } = show;

  // Filter out links with empty url or text
  const validLinks = links?.filter(link => link.url && link.text) || [];

  const ShowLogoOrTitle = () => {
    const showWhiteLogo = images?.['white-logo-41'];

    return (
      <h2 className={styles.show_hero_show_title_logo}>
        <Link href={`/show/${slug}/`}>
        { showWhiteLogo ? (
          <ITSImage
            src={showWhiteLogo}
            alt={title}
            width={240}
          />
        ) : (
        <>
        {title}
        </>
        )}
        </Link>
      </h2>
    )
  }

  const ShowHeroKabobMenuItems = () => (
    <ul>
      <li>
        <MyListButton
          style='kabobMenu'
          show={show}
        />
      </li>
      <li className={styles.show_hero_kabob_item_explore_the_show}>
        <Link href={`/show/${slug}/`}>
          <PlayIcon />
          <span>Explore the Show</span>
        </Link>
      </li>
      {validLinks.map((link, index) => (
        <li className={styles.show_hero_kabob_item_link} key={index}>
          <Link href={link.url} target="_blank" rel="noopener noreferrer">
            <ArrowRightIcon />
            <span>{link.text}</span>
          </Link>
        </li>
      ))}
    </ul>
  )

  const ShowHeroOverlay = (props: { isLoaded: boolean}) => {
    const { isLoaded } = props;
    return (
      <div className={isLoaded ? styles.show_hero_loaded_overlay : styles.show_hero_initial_overlay}>
        <ShowLogoOrTitle />
        <p className={styles.show_hero_show_description}>
          {description_short}
        </p>
        <div className={isLoaded ? styles.show_hero_loaded_overlay_buttons : styles.show_hero_initial_overlay_buttons}>
          <LinkButton
            href={`/show/${slug}/`}
            style="white"
          >
            Explore the Show
          </LinkButton>

          <MyListButton show={show} style='iconOnly' />

          <KabobMenu
            className={styles.show_hero_kabob_button_above_sm}
            contentClassName={styles.show_hero_kabob_content_above_sm}
          >
            <ShowHeroKabobMenuItems />
          </KabobMenu>
        </div>
        <KabobMenu
          className={styles.show_hero_kabob_button_below_sm}
          contentClassName={styles.show_hero_kabob_content_below_sm}
        >
          <ShowHeroKabobMenuItems />
        </KabobMenu>
      </div>
    )
  }

  const InitialShowHero = () => {
    // Image order is:
    // 1. Override image from curate object
    // 2. Show background image (as set in Media Manager)
    // 3. The Preview's mezzanine image
    // 4. `null` - which will trigger a blue background in <MezzanineVideoInitializer />
    const imageToUse =
      background_image ||
      images?.background ||
      featuredPreview?.mezzanine16x9ImageUrl ||
      null;

    return (
      <div className={styles.show_hero_initial}>
        <MezzanineVideoInitializer
          videoType={featuredPreview?.videoType.toLowerCase() as VideoTypeEnum || VideoTypeEnum.Video }
          imgSrc={imageToUse}
          alt={featuredPreview?.title || title}
          showWatchButton={Boolean(featuredPreview)}
          onClick={() => setShowVideo(true)}
          verticalOffsetButton={true}
          width={1700}
          className={styles.show_hero_video_initializer}
        />
        <ShowHeroOverlay isLoaded={false} />
      </div>
  )};

  const VideoPlayerLoaded = () => {
    const playerConfig = {
      autoplay: true,
      embedType: isSVP ? 'stationplayer' : 'portalplayer',
      disableContinuousPlay: true,
      previewLayout: true,
      stationId: stationData?.id,
      callsign: stationData?.attributes.call_sign,
    }

    return (
      <div className={styles.show_hero_loaded}>
        { featuredPreview && (
          <VideoPlayer
            video={featuredPreview}
            playerConfig={playerConfig}
            className={styles.show_hero_loaded_video_player}
          />
        )}

        <ShowHeroOverlay isLoaded={true} />
      </div>
    )
  };

  return (
    showVideo && featuredPreview ?
      (<section>
        <VideoPlayerLoaded />
      </section>) :
      (<section>
        <InitialShowHero />
      </section>)
  );
}

export default ShowHero;
